import { useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Bell, Clock, LogOut, Settings, User, X, ShieldAlert, ArrowLeft } from 'lucide-react'
import { Avatar } from './Avatar'
import { MenuIcon, SearchIcon } from './Icons'
import { SearchInput } from './SearchInput'
import { NotificationPanel } from './NotificationPanel'
import { useAuthStore } from '../features/auth/hooks/useAuthStore'
import { authApi } from '../api/auth/auth.api'
import { useOrganizationProfile } from '../shared/hooks/useOrganizationProfile'
import { LogoutConfirmModal } from './LogoutConfirmModal'
import { isOrganizationAdminRole } from '../routes/auth'
import {
  useNotifications,
  useUnreadCount,
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
} from '../hooks/useNotifications'
import { showToastMessage } from './Toast'

interface HeaderProps {
  onMenuClick?: () => void
  pageTitle?: string
  breadcrumb?: string[] | string
  userName?: string
  userRole?: string
  avatarInitials?: string
  avatarSrc?: string
  hideSidebar?: boolean
  hideBreadcrumb?: boolean
}

type SearchItem = {
  id: string
  title: string
  subtitle: string
  path: string
  type: string
  permission?: string
}

// Navigable Teacher Modules & Quick Actions
const teacherSearchItems: SearchItem[] = [
  // Main
  { id: 'teacher-dashboard', title: 'Dashboard', subtitle: 'Overview dashboard, schedule, and daily tools', path: '/teacher/dashboard', type: 'Main', permission: 'dashboard:view' },

  // Classroom Management & Attendance
  { id: 'teacher-myclasses', title: 'My Classes', subtitle: 'Manage your assigned classes, students and timetable', path: '/teacher/myclasses', type: 'Classroom', permission: 'class-management:view' },
  { id: 'teacher-attendance', title: 'Student Attendance', subtitle: 'Mark and view daily student classroom attendance', path: '/teacher/attendence', type: 'Classroom', permission: 'attendance:view' },
  { id: 'teacher-staff-attendance', title: 'Staff Attendance', subtitle: 'View your staff check-in/check-out records and regularization', path: '/teacher/attendance', type: 'Attendance', permission: 'dashboard:view' },

  // Assignments & Homework
  { id: 'teacher-assignments', title: 'Assignments & Homework', subtitle: 'Create homework and review student submissions', path: '/teacher/assignments', type: 'Classroom', permission: 'assignments:view' },
  { id: 'teacher-assignments-create', title: 'Create Homework Assignment', subtitle: 'Post new homework or assignment to class', path: '/teacher/assignments/create', type: 'Action', permission: 'assignments:create' },
  { id: 'teacher-assignments-review', title: 'Review Submissions', subtitle: 'Evaluate student submitted homework and assign marks', path: '/teacher/assignments/review', type: 'Action', permission: 'assignments:view' },
  { id: 'teacher-assignments-report', title: 'Assignment Report', subtitle: 'View submission progress reports and completion stats', path: '/teacher/assignments/report', type: 'Report', permission: 'assignments:view' },
  { id: 'teacher-homework', title: 'Homework', subtitle: 'Manage and track class homework assignments', path: '/teacher/homework', type: 'Classroom', permission: 'assignments:view' },
  { id: 'teacher-homework-create', title: 'Create Homework', subtitle: 'Assign new homework task with attachments', path: '/teacher/homework/create', type: 'Action', permission: 'assignments:view' },

  // Lesson Plans
  { id: 'teacher-lessonplans', title: 'Lesson Plans', subtitle: 'Manage daily teaching lesson plans and curriculum progress', path: '/teacher/lessonplans', type: 'Classroom', permission: 'lesson-plans:view' },
  { id: 'teacher-lessonplans-create', title: 'Create Lesson Plan', subtitle: 'Draft a new teaching lesson plan with objectives', path: '/teacher/lessonplans/create', type: 'Action', permission: 'lesson-plans:create' },

  // Examinations & Student Performance
  { id: 'teacher-examinations', title: 'Examinations & Marks', subtitle: 'Enter and review student exam scores and evaluations', path: '/teacher/examinations', type: 'Academics', permission: 'examinations:view' },
  { id: 'teacher-exam-management', title: 'Exam Management', subtitle: 'Enter marks, submit grades and review schedules', path: '/teacher/exam-management', type: 'Academics', permission: 'assignments:view' },
  { id: 'teacher-enter-marks', title: 'Enter Marks', subtitle: 'Record student exam subject marks', path: '/teacher/exam-management/enter-marks', type: 'Action', permission: 'assignments:view' },
  { id: 'teacher-student-performance', title: 'Student Performance', subtitle: 'Analyze student grades, strengths, and academic analytics', path: '/teacher/student-performance', type: 'Academics', permission: 'examinations:view' },

  // Leave Management
  { id: 'teacher-leave-management', title: 'Apply Leave', subtitle: 'Request leaves and view leave balance & history', path: '/teacher/leave-management', type: 'Academic', permission: 'leave:apply' },
  { id: 'teacher-leave-history', title: 'Leave History', subtitle: 'View past applied leaves and status', path: '/teacher/leave-management/history', type: 'Academic', permission: 'leave:apply' },
  { id: 'teacher-leave-calendar', title: 'Leave Calendar', subtitle: 'Calendar view of leaves and holidays', path: '/teacher/leave-management/calendar', type: 'Academic', permission: 'leave:apply' },

  // Communication
  { id: 'teacher-communication', title: 'Communication Dashboard', subtitle: 'View school-wide notices, announcements and updates', path: '/teacher/communication', type: 'Communication', permission: 'communication:view' },
  { id: 'teacher-messages', title: 'Messages', subtitle: 'Direct messages and conversations with staff and parents', path: '/teacher/communication/message', type: 'Communication', permission: 'communication:view' },
  { id: 'teacher-compose-message', title: 'Compose Message', subtitle: 'Send a new message or announcement', path: '/teacher/communication/compose', type: 'Action', permission: 'communication:view' },
  { id: 'teacher-notices', title: 'Notices', subtitle: 'School circulars, teacher updates, and notice board', path: '/teacher/communication/notices', type: 'Communication', permission: 'communication:view' },
  { id: 'teacher-groups', title: 'Groups', subtitle: 'Class, department, and teacher collaborative groups', path: '/teacher/communication/groups', type: 'Communication', permission: 'communication:view' },

  // Library & Resources
  { id: 'teacher-library', title: 'Library', subtitle: 'Access the digital catalog and library books', path: '/teacher-library', type: 'Resources', permission: 'library:view' },
  { id: 'teacher-browse-books', title: 'Browse Books', subtitle: 'Explore available school library catalog', path: '/teacher-library/browse-books', type: 'Resources', permission: 'library:view' },
  { id: 'teacher-my-books', title: 'My Books', subtitle: 'View books currently borrowed and due dates', path: '/teacher-library/my-books', type: 'Resources', permission: 'library:view' },

  // Profile & Settings
  { id: 'teacher-profile', title: 'Teacher Profile', subtitle: 'View and update profile information and credentials', path: '/teacher/profile', type: 'Profile', permission: 'profile:view' },
  { id: 'teacher-settings', title: 'Teacher Settings', subtitle: 'Account settings, notification preferences and password', path: '/teacher/settings', type: 'Settings', permission: 'settings:view' },
  { id: 'teacher-change-password', title: 'Change Password', subtitle: 'Update account password and security', path: '/teacher/settings/change-password', type: 'Settings', permission: 'settings:view' },
]

// Navigable School Admin Modules & Quick Actions (100% Verified Routes)
const adminSearchItems: SearchItem[] = [
  // Main
  { id: 'admin-dashboard', title: 'Dashboard', subtitle: 'Overview analytics, stats, and school activities', path: '/admin/dashboard', type: 'Main', permission: 'dashboard:view' },

  // Front Office
  { id: 'admin-frontoffice-enquiries', title: 'Admission Enquiries', subtitle: 'Manage student admission inquiries and follow-ups', path: '/admin/frontoffice/admission-enquiries', type: 'Front Office', permission: 'dashboard:view' },
  { id: 'admin-admission-application', title: 'Admission Application', subtitle: 'Process new student admission applications', path: '/admin/admission-application', type: 'Front Office', permission: 'admission-application:view' },
  { id: 'admin-admission-process', title: 'Admission Process', subtitle: 'Review and confirm admissions workflow', path: '/admin/admission-process', type: 'Front Office', permission: 'admission-process:view' },
  { id: 'admin-seat-planning', title: 'Seat Management', subtitle: 'Plan section capacities and seat allocation', path: '/seat-planning', type: 'Front Office', permission: 'dashboard:view' },
  { id: 'admin-frontoffice-staff-dir', title: 'Staff Directory', subtitle: 'View teaching and non-teaching staff directory', path: '/admin/frontoffice/staff-directory', type: 'Front Office', permission: 'staff-directory:view' },

  // Visitor Management
  { id: 'admin-visitor-dashboard', title: 'Visitor Dashboard', subtitle: 'Campus visitor analytics and today visits', path: '/admin/frontoffice/visitor-management', type: 'Visitor Management', permission: 'dashboard:view' },
  { id: 'admin-visitor-entry', title: 'Visitor Entry', subtitle: 'Check in walk-in and expected campus visitors', path: '/admin/frontoffice/visitor-management/new', type: 'Visitor Management', permission: 'dashboard:view' },
  { id: 'admin-visitor-log', title: 'Visitor Log', subtitle: 'Historical record of visitor entries and check-outs', path: '/admin/frontoffice/visitor-management/log', type: 'Visitor Management', permission: 'dashboard:view' },

  // Student Management
  { id: 'admin-student-directory', title: 'Student Directory', subtitle: 'Search and manage student profiles and records', path: '/admin/student-directory', type: 'Student Management', permission: 'student-directory:view' },
  { id: 'admin-add-student-wizard', title: 'Add Student', subtitle: 'Add a new student admission wizard', path: '/admin/add-student', type: 'Student Management', permission: 'student-directory:view' },
  { id: 'admin-student-lifecycle', title: 'Student Lifecycle', subtitle: 'Manage promotion, transfers and student transitions', path: '/admin/student-lifecycle', type: 'Student Management', permission: 'student-directory:view' },
  { id: 'admin-doc-verification', title: 'Student Doc Verification', subtitle: 'Verify applicant uploaded documents and certificates', path: '/admin/document-verification', type: 'Student Management', permission: 'document-verification:view' },
  { id: 'admin-student-attendance', title: 'Student Attendance', subtitle: 'Monitor daily student classroom attendance', path: '/teacher/attendence', type: 'Student Management', permission: 'attendance:view' },
  { id: 'admin-student-reports', title: 'Student Reports', subtitle: 'Student enrollment and demographic analytics', path: '/admin/student-reports', type: 'Student Management', permission: 'dashboard:view' },

  // Library Management
  { id: 'admin-library-dashboard', title: 'Library Dashboard', subtitle: 'Library overview, active loans, and book statistics', path: '/admin-library/dashboard', type: 'Library Management', permission: 'library:view' },
  { id: 'admin-library-catalogue', title: 'Library Catalogue', subtitle: 'Search library collection and book catalog', path: '/admin-library/catalogue', type: 'Library Management', permission: 'library:view' },
  { id: 'admin-library-inventory', title: 'Inventory Management', subtitle: 'Add books, stock tracking, and book barcodes', path: '/admin-library/inventory', type: 'Library Management', permission: 'library:view' },
  { id: 'admin-library-issue-return', title: 'Issue / Return', subtitle: 'Circulation desk: issue books and process returns', path: '/admin-library/issue-return', type: 'Library Management', permission: 'library:view' },
  { id: 'admin-library-reserve-book', title: 'Reserve Book', subtitle: 'Student and teacher book reservation requests', path: '/admin-library/reserve-book', type: 'Library Management', permission: 'library:view' },
  { id: 'admin-library-fine-management', title: 'Fine Management', subtitle: 'Track and collect overdue library book fines', path: '/admin-library/fine-management', type: 'Library Management', permission: 'library:view' },
  { id: 'admin-library-reports', title: 'Library Reports', subtitle: 'Book circulation, inventory and overdue reports', path: '/admin-library/reports', type: 'Library Management', permission: 'library:view' },
  { id: 'teacher-library-home', title: 'Library Home', subtitle: 'Digital library catalog and resources', path: '/teacher-library', type: 'Library Management', permission: 'library:view' },

  // Academic Management
  { id: 'admin-academics', title: 'Academics', subtitle: 'Academic years, grades, terms and curriculum configuration', path: '/admin/academics', type: 'Academic Management', permission: 'academics:view' },
  { id: 'admin-events-calendar', title: 'Events Calendar', subtitle: 'Academic calendar, school holidays and events schedule', path: '/admin/events-calendar', type: 'Academic Management', permission: 'academics:view' },
  { id: 'admin-subject-management', title: 'Subject Management', subtitle: 'Configure academic subjects and grade curricula', path: '/admin/academics/subject-management', type: 'Academic Management', permission: 'subjects:view' },
  { id: 'admin-subject-allocation', title: 'Subject Allocation', subtitle: 'Assign subjects and teachers to classes and sections', path: '/admin/class-management/subject-allocation', type: 'Academic Management', permission: 'subjects:view' },
  { id: 'admin-timetable', title: 'Timetable', subtitle: 'Master class schedules and teacher periods', path: '/academics/timetable', type: 'Academic Management', permission: 'timetable:view' },
  { id: 'admin-assignments', title: 'Assignments', subtitle: 'View classroom homework and project assignments', path: '/teacher/assignments', type: 'Academic Management', permission: 'assignments:view' },
  { id: 'admin-homework', title: 'Homework', subtitle: 'Student daily homework tasks and submissions', path: '/teacher/homework', type: 'Academic Management', permission: 'assignments:view' },
  { id: 'admin-lessonplans', title: 'Lesson Plans', subtitle: 'Teacher lesson plans and curriculum progress', path: '/teacher/lessonplans', type: 'Academic Management', permission: 'lesson-plans:view' },

  // HRMS
  { id: 'admin-hrms-dashboard', title: 'HRMS Dashboard', subtitle: 'Staff overview, headcount, attendance and recruitment', path: '/admin/hrms/dashboard', type: 'HRMS', permission: 'dashboard:view' },
  { id: 'admin-hrms-onboarding', title: 'HRMS Onboarding', subtitle: 'Onboard new staff and employee documentation', path: '/admin/hrms/onboarding', type: 'HRMS', permission: 'dashboard:view' },
  { id: 'admin-hrms-new-staff-dir', title: 'Staff Directory New', subtitle: 'Updated staff profile directory with advanced filtering', path: '/admin/hrms/new-staff-directory', type: 'HRMS', permission: 'staff-directory:view' },
  { id: 'admin-hrms-staff-docs', title: 'Staff Documents', subtitle: 'Employee contracts, certifications and verification', path: '/admin/hrms/staff-documents', type: 'HRMS', permission: 'dashboard:view' },
  { id: 'admin-hrms-attendance-leave', title: 'Attendance & Leave', subtitle: 'Staff leave applications, approvals and attendance records', path: '/admin/hrms/attendance-leave', type: 'HRMS', permission: 'dashboard:view' },
  { id: 'admin-hrms-staff-attendance', title: 'Staff Attendance', subtitle: 'Daily teacher and staff check-in/check-out logs', path: '/teacher/attendance', type: 'HRMS', permission: 'dashboard:view' },
  { id: 'admin-hrms-payroll', title: 'Payroll', subtitle: 'Salary processing, pay slips and payroll disbursement', path: '/admin/hrms/payroll', type: 'HRMS', permission: 'dashboard:view' },
  { id: 'admin-hrms-reports', title: 'Employee Reports', subtitle: 'HR analytics, attendance summaries and payroll reports', path: '/admin/hrms/employee-reports', type: 'HRMS', permission: 'dashboard:view' },

  // Examination
  { id: 'admin-examinations', title: 'Examination', subtitle: 'Comprehensive exam management and marks configuration', path: '/admin/academic/examinations', type: 'Examination', permission: 'academics:view' },
  { id: 'admin-exam-config', title: 'Exam Configuration', subtitle: 'Configure examination terms, grading scales and types', path: '/admin/academic/examinations/exam-configuration', type: 'Examination', permission: 'academics:view' },
  { id: 'admin-exam-schedule', title: 'Exam Schedule', subtitle: 'Create exam timetables, dates and hall allocations', path: '/admin/academic/examinations/exam-schedule', type: 'Examination', permission: 'academics:view' },
  { id: 'admin-marks-entry', title: 'Marks Entry', subtitle: 'Enter and update student examination scores and grades', path: '/admin/academic/examinations/marks-entry', type: 'Examination', permission: 'academics:view' },
  { id: 'admin-report-card', title: 'Report Card', subtitle: 'Generate and publish student academic report cards', path: '/admin/academic/examinations/report-card', type: 'Examination', permission: 'academics:view' },
  { id: 'admin-performance-reports', title: 'Performance Reports', subtitle: 'Classroom and subject academic performance analytics', path: '/admin/academic/examinations/performance', type: 'Examination', permission: 'academics:view' },
  { id: 'teacher-exam-management', title: 'Exam Management', subtitle: 'Teacher exam evaluation and result entry', path: '/teacher/exam-management', type: 'Examination', permission: 'assignments:view' },

  // Class Management
  { id: 'admin-seat-management', title: 'Class & Section Setup', subtitle: 'Class & section setup and capacity management', path: '/admin/enrollment/class-section-setup', type: 'Class Management', permission: 'seat-management:view' },
  { id: 'admin-myclasses', title: 'My Classes', subtitle: 'Class sections and student enrollment list', path: '/teacher/myclasses', type: 'Class Management', permission: 'class-management:view' },
  { id: 'admin-classes', title: 'Classes', subtitle: 'Section division and class allocation', path: '/admin/class-management/classes', type: 'Class Management', permission: 'class-management:view' },
  { id: 'admin-class-reports', title: 'Class Reports', subtitle: 'Class analytics and section occupancy reports', path: '/admin/class-management/class-reports', type: 'Class Management', permission: 'class-management:view' },
  { id: 'admin-pending-queue', title: 'Pending Queue', subtitle: 'Student section allocation queue', path: '/admin/class-management/pending-queue', type: 'Class Management', permission: 'class-management:view' },

  // Finance Management
  { id: 'admin-fees-finance', title: 'Fees & Finance', subtitle: 'Fee structures, collect payments, discounts, reports', path: '/admin/finance/fees-finance', type: 'Finance Management', permission: 'finance:view' },
  { id: 'admin-fee-overview', title: 'Fee Overview', subtitle: 'Financial KPIs, collections overview and dues summary', path: '/admin/finance/fees-finance/overview', type: 'Finance Management', permission: 'finance:view' },
  { id: 'admin-fee-head', title: 'Fee Heads', subtitle: 'Master definition of fee components', path: '/admin/finance/fees-finance/fee-head', type: 'Finance Management', permission: 'finance:view' },
  { id: 'admin-fee-structure', title: 'Fee Structure', subtitle: 'Class fee structure configurations', path: '/admin/finance/fees-finance/fee-structure', type: 'Finance Management', permission: 'finance:view' },
  { id: 'admin-fee-payments', title: 'Fee Collection & Payments', subtitle: 'Record student fee payments and issue receipts', path: '/admin/finance/fees-finance/payments', type: 'Finance Management', permission: 'finance:view' },
  { id: 'admin-fee-discounts', title: 'Fee Discounts', subtitle: 'Manage student fee discount types and concessions', path: '/admin/finance/fees-finance/discounts', type: 'Finance Management', permission: 'finance:view' },
  { id: 'admin-fee-reports', title: 'Fee Reports', subtitle: 'Financial collection, dues and reconciliation reports', path: '/admin/finance/fees-finance/reports', type: 'Finance Management', permission: 'finance:view' },
  { id: 'admin-fee-approvals', title: 'Fee Approvals', subtitle: 'Review and approve fee concessions and refunds', path: '/admin/finance/fees-finance/approvals', type: 'Finance Management', permission: 'finance:view' },

  // Communication
  { id: 'admin-communication', title: 'Communication Dashboard', subtitle: 'Communication overview, messages, notices, and groups', path: '/communication', type: 'Communication', permission: 'communication:view' },
  { id: 'admin-communication-message', title: 'Message', subtitle: 'Direct messages and conversations with staff and parents', path: '/communication/message', type: 'Communication', permission: 'communication:view' },
  { id: 'admin-communication-notices', title: 'Notices', subtitle: 'School-wide notice board and circulars', path: '/communication/notices', type: 'Communication', permission: 'communication:view' },
  { id: 'admin-communication-groups', title: 'Groups', subtitle: 'Manage student, parent and teacher groups', path: '/communication/groups', type: 'Communication', permission: 'communication:view' },

  // Transport Management
  { id: 'admin-transport-routes', title: 'Route & Stop Management', subtitle: 'Bus routes, pickup/drop stops, and route timings', path: '/admin/transport/routes', type: 'Transport Management', permission: 'transport:view' },
  { id: 'admin-transport-vehicles', title: 'Vehicle Management', subtitle: 'School fleet, bus details, maintenance and permits', path: '/admin/transport/vehicles', type: 'Transport Management', permission: 'transport:view' },
  { id: 'admin-transport-drivers', title: 'Driver & Attendant Management', subtitle: 'Drivers, bus attendants, licenses and contact info', path: '/admin/transport/driver-attendants', type: 'Transport Management', permission: 'transport:view' },
  { id: 'admin-transport-allocation', title: 'Student Transport Allocation', subtitle: 'Assign students to bus routes and designated stops', path: '/admin/transport/student-allocation', type: 'Transport Management', permission: 'transport:view' },
  { id: 'admin-transport-fee', title: 'Transport Fee', subtitle: 'Transport fee slabs, distance bands, and billing', path: '/admin/transport/fee-bands', type: 'Transport Management', permission: 'transport:view' },
  { id: 'admin-transport-reports', title: 'Transport Reports & Alerts', subtitle: 'Route occupancy, fleet reports and safety alerts', path: '/admin/transport/reports', type: 'Transport Management', permission: 'transport:view' },

  // User & Role Management
  { id: 'admin-user-management', title: 'User Management', subtitle: 'System users, staff accounts and login credentials', path: '/admin/user-management', type: 'User & Role Management', permission: 'user-management:view' },
  { id: 'admin-role-management', title: 'Role Management', subtitle: 'Configure user roles & granular permissions grid', path: '/admin/user-management/role-management', type: 'User & Role Management', permission: 'role-management:view' },
  { id: 'admin-departments', title: 'Departments', subtitle: 'Manage school academic and administrative departments', path: '/admin/user-management/departments', type: 'User & Role Management', permission: 'departments:view' },
  { id: 'admin-designations', title: 'Designations', subtitle: 'Staff job titles and organizational designations', path: '/admin/user-management/designations', type: 'User & Role Management', permission: 'designations:view' },
  { id: 'admin-audit-logs', title: 'Audit Logs', subtitle: 'Track administrative events, security changes, and mutations', path: '/admin/audit-logs', type: 'User & Role Management', permission: 'audit-logs:view' },

  // Organization Admin (when accessible)
  { id: 'org-admin-dashboard', title: 'Organization Dashboard', subtitle: 'Multi-branch overview, metrics, and administration', path: '/organization-admin/dashboard', type: 'Organization Admin', permission: 'dashboard:view' },
  { id: 'org-admin-organization', title: 'Manage Organization', subtitle: 'Organization profile, branding, and central settings', path: '/organization-admin/manage-organization', type: 'Organization Admin', permission: 'organization:view' },
  { id: 'org-admin-branches', title: 'Manage Branches', subtitle: 'Branch campuses, regional locations and branch setup', path: '/organization-admin/manage-branches', type: 'Organization Admin', permission: 'branches:view' },
  { id: 'org-admin-user-management', title: 'Org User Management', subtitle: 'Organization-level administrative users', path: '/organization-admin/user-management', type: 'Organization Admin', permission: 'user-management:view' },
  { id: 'org-admin-role-management', title: 'Org Role Management', subtitle: 'Organization-wide role profiles and permissions', path: '/organization-admin/role-management', type: 'Organization Admin', permission: 'role-management:view' },
  { id: 'org-admin-audit-logs', title: 'Org Audit Logs', subtitle: 'Multi-branch audit trail and compliance ledger', path: '/organization-admin/audit-logs', type: 'Organization Admin', permission: 'audit-logs:view' },

  // System Settings & Profile
  { id: 'admin-settings', title: 'School Settings', subtitle: 'General configuration, branding, security and defaults', path: '/admin/settings', type: 'System', permission: 'settings:view' },
  { id: 'admin-profile', title: 'Admin Profile', subtitle: 'View and update admin profile details and password', path: '/admin/profile', type: 'System', permission: 'profile:view' },
]

export function getTeacherBreadcrumbParts(pathname: string): [string, string] {
  if (pathname.includes('/assignments/create')) return ['Classroom Management', 'Create Assignment']
  if (pathname.includes('/assignments/review')) return ['Classroom Management', 'Review Submissions']
  if (pathname.includes('/assignments/report')) return ['Classroom Management', 'Assignment Report']
  if (pathname.includes('/homework')) return ['Classroom Management', 'Homework']
  if (pathname.includes('/assignments')) return ['Classroom Management', 'Assignments & Homework']
  if (pathname.includes('/myclasses') || pathname.includes('/classes')) return ['Classroom Management', 'My Classes']
  if (pathname.includes('/attendence') || pathname.includes('/attendance')) return ['Classroom Management', 'Attendance']
  if (pathname.includes('/lessonplans/create') || pathname.includes('/create-lesson-plan')) return ['Classroom Management', 'Create Lesson Plan']
  if (pathname.includes('/lessonplans/edit')) return ['Classroom Management', 'Edit Lesson Plan']
  if (pathname.includes('/lessonplans/duplicate')) return ['Classroom Management', 'Duplicate Lesson Plan']
  if (pathname.includes('/lessonplans/sections')) return ['Classroom Management', 'Lesson Plan Sections']
  if (pathname.includes('/lessonplans/detail') || pathname.match(/\/lessonplans\/[^/]+/)) return ['Classroom Management', 'Plan Details']
  if (pathname.includes('/lessonplans')) return ['Classroom Management', 'Lesson Plans']
  if (pathname.includes('/examinations')) return ['Academic Management', 'Examinations & Marks']
  if (pathname.includes('/student-performance')) return ['Academic Management', 'Student Performance']
  if (pathname.includes('/leave-management')) return ['Academic Management', 'Apply Leave']
  if (pathname.includes('/announcements')) return ['Communication', 'Announcements']
  if (pathname.includes('/library')) return ['Resources', 'Library']
  return ['Main', 'Teacher Dashboard']
}

export function getAdminBreadcrumbParts(pathname: string): string[] {
  if (pathname.includes('/audit-logs') || pathname.includes('/auditlogs')) return ['Audit Logs']
  if (pathname.includes('/organization-admin/manage-organization/edit')) return ['Organization & Branch Setup', 'Manage Organization', 'Edit Organization']
  if (pathname.includes('/organization-admin/manage-organization')) return ['Organization & Branch Setup', 'Manage Organization']
  if (pathname.includes('/organization-admin/manage-branches')) return ['Organization & Branch Setup', 'Manage Branches']
  if (pathname.includes('/organization-admin/user-management')) return ['User Management']
  if (pathname.includes('/organization-admin/role-management')) return ['Role Management']
  if (pathname.includes('/organization-admin/settings')) return ['Organization Admin', 'Settings']
  if (pathname.includes('/organization-admin/profile')) return ['Organization Admin', 'Profile']
  if (pathname.includes('/organization-admin')) return ['Organization & Branch Setup', 'Organization Admin']

  const seatDetailMatch = pathname.match(/\/seat-planning\/([^/]+)\/([^/]+)/)
  if (seatDetailMatch && seatDetailMatch[1] && seatDetailMatch[2]) {
    const rawClass = decodeURIComponent(seatDetailMatch[1]).replace(/^grade-/, 'Grade ').replace(/-/g, ' ')
    const classLabel = rawClass.split(' ').map((s) => s.charAt(0).toUpperCase() + s.slice(1)).join(' ')
    const rawSec = decodeURIComponent(seatDetailMatch[2])
    const secLabel = rawSec.toLowerCase().startsWith('sec-')
      ? `Section ${rawSec.slice(4).toUpperCase()}`
      : rawSec.split('-').map((s) => s.charAt(0).toUpperCase() + s.slice(1)).join(' ')
    return ['Front Office', 'Seat Management', `${classLabel} — ${secLabel}`]
  }
  if (pathname.includes('/seat-planning')) return ['Front Office', 'Seat Management']

  if (pathname.includes('/visitor-management/log')) return ['Visitor Management', 'Visitor Log']
  if (pathname.includes('/visitor-management/new/walk-in')) return ['Visitor Management', 'Visitor Entry', 'Walk-in Visitor']
  if (pathname.includes('/visitor-management/new/expected/register')) return ['Visitor Management', 'Visitor Entry', 'Expected Visitor', 'Pre-register']
  if (pathname.includes('/visitor-management/new/expected')) return ['Visitor Management', 'Visitor Entry', 'Expected Visitor']
  if (pathname.includes('/visitor-management/new')) return ['Visitor Management', 'Visitor Entry']
  if (pathname.includes('/visitor-management')) return ['Visitor Management', 'Dashboard']
  if (pathname.includes('/admission-enquiries/new')) return ['Front Office', 'Admission Enquiry', 'New Enquiry']
  const editMatch = pathname.match(/\/admission-enquiries\/([^/]+)\/edit/)
  if (editMatch && editMatch[1]) {
    const rawId = decodeURIComponent(editMatch[1])
    const formattedId = rawId.startsWith('#') ? rawId : `#${rawId}`
    return ['Front Office', 'Admission Enquiry', formattedId, 'Edit Enquiry']
  }
  const convertMatch = pathname.match(/\/admission-enquiries\/([^/]+)\/convert/)
  if (convertMatch && convertMatch[1]) {
    const rawId = decodeURIComponent(convertMatch[1])
    const formattedId = rawId.startsWith('#') ? rawId : `#${rawId}`
    return ['Front Office', 'Admission Enquiry', formattedId, 'Convert to Application']
  }
  const enquiryDetailMatch = pathname.match(/\/admission-enquiries\/([^/]+)/)

  if (enquiryDetailMatch && enquiryDetailMatch[1] && enquiryDetailMatch[1] !== 'new') {
    const rawId = decodeURIComponent(enquiryDetailMatch[1])
    const formattedId = rawId.startsWith('#') ? rawId : `#${rawId}`
    return ['Front Office', 'Admission Enquiry', formattedId]
  }

  if (pathname.includes('/hrms/employee-reports/attendance')) return ['HRMS', 'Employee Reports', 'Attendance Summary']
  if (pathname.includes('/hrms/employee-reports/leave')) return ['HRMS', 'Employee Reports', 'Leave Summary']
  if (pathname.includes('/hrms/employee-reports/payroll')) return ['HRMS', 'Employee Reports', 'Payroll Summary']
  if (pathname.includes('/hrms/employee-reports/documents')) return ['HRMS', 'Employee Reports', 'Staff Document Reports']
  if (pathname.includes('/hrms/employee-reports')) return ['HRMS', 'Employee Reports']
  if (pathname.includes('/hrms/staff-documents/')) return ['HRMS', 'Staff Documents', 'Employee Documents']
  if (pathname.includes('/hrms/staff-documents')) return ['HRMS', 'Staff Documents']
  if (pathname.includes('/teacher/attendance')) return ['Teacher', 'My Attendance']
  if (pathname.includes('/hrms/attendance-leave')) return ['HRMS', 'Attendance & Leave']
  if (pathname.includes('/hrms/new-staff-directory/add-staff/employment')) return ['HRMS', 'Staff Directory New', 'Employment Details']
  if (pathname.includes('/hrms/new-staff-directory/add-staff/documents')) return ['HRMS', 'Staff Directory New', 'Documents']
  if (pathname.includes('/hrms/new-staff-directory/add-staff/payroll')) return ['HRMS', 'Staff Directory New', 'Payroll & Bank']
  if (pathname.includes('/hrms/new-staff-directory/add-staff/review')) return ['HRMS', 'Staff Directory New', 'Review & Offer']
  if (pathname.includes('/hrms/new-staff-directory/add-staff/acceptance')) return ['HRMS', 'Staff Directory New', 'Acceptance']
  if (pathname.includes('/hrms/new-staff-directory/add-staff/completed')) return ['HRMS', 'Staff Directory New', 'Completed']
  if (pathname.includes('/hrms/new-staff-directory/add-staff')) return ['HRMS', 'Staff Directory New', 'Basic Information']
  if (pathname.includes('/hrms/new-staff-directory')) return ['HRMS', 'Staff Directory New']
  if (pathname.includes('/hrms/onboarding/new/employment')) return ['HRMS', 'Onboarding', 'Employment Details']
  if (pathname.includes('/hrms/onboarding/new/documents')) return ['HRMS', 'Onboarding', 'Documents']
  if (pathname.includes('/hrms/onboarding/new/payroll')) return ['HRMS', 'Onboarding', 'Payroll & Bank']
  if (pathname.includes('/hrms/onboarding/new/review')) return ['HRMS', 'Onboarding', 'Review & Offer']
  if (pathname.includes('/hrms/onboarding/new/acceptance')) return ['HRMS', 'Onboarding', 'Acceptance']
  if (pathname.includes('/hrms/onboarding/new/completed')) return ['HRMS', 'Onboarding', 'Completed']
  if (pathname.includes('/hrms/onboarding/new')) return ['HRMS', 'Onboarding', 'Basic Information']
  if (pathname.includes('/hrms/onboarding')) return ['HRMS', 'Onboarding']
  if (pathname.includes('/hrms/dashboard')) return ['HRMS', 'Dashboard']
  if (pathname.includes('/hrms/payroll')) return ['HRMS', 'Payroll']
  if (pathname.includes('/hrms')) return ['HRMS', 'Staff Documents']
  if (pathname.includes('/admission-enquiries')) return ['Front Office', 'Admission Enquiry']



  if (pathname.includes('/student-directory/add') || pathname.includes('/add-student')) return ['Front Office', 'Add Student']
  if (
    pathname.includes('/student-lifecycle') ||
    pathname.includes('/transfer-students') ||
    pathname.includes('/promote-students')
  ) return ['Student Management', 'Student Lifecycle']
  if (pathname.includes('/student-directory')) return ['Student Management', 'Student Directory']
  if (pathname.includes('/document-verification')) return ['Student Management', 'Student Doc Verification']
  if (pathname.includes('/enrollment/class-section-setup') || pathname.includes('/enrollment/seat-management')) return ['Class Management', 'Class & Section Setup']
  if (pathname.includes('/class-management/class-reports')) return ['Class Management', 'Class Reports']
  if (pathname.includes('/enrollment')) return ['Front Office', 'Enrollment']
  if (pathname.includes('/admission')) return ['Front Office', 'Front Office']
  if (pathname.includes('/interview')) return ['Front Office', 'Interviews']
  if (pathname.includes('/finance/fee-structure')) return ['Finance Management', 'Fee Structure']
  if (pathname.includes('/finance')) return ['Finance Management', 'Fees & Finance']
  if (pathname.includes('/academics/subject-management')) return ['Academic Management', 'Subject Management']
  if (pathname.includes('/academics/timetable')) return ['Academic Management', 'Timetable']
  if (pathname.includes('/academics')) return ['Academic Management', 'Academics']
  if (pathname.includes('/class-management/pending-queue')) return ['Class Management', 'Pending Queue']
  if (pathname.includes('/class-management/classes')) return ['Class Management', 'Classes']
  if (pathname.includes('/academic/examinations/timetable')) return ['Examination Management', 'Exam Timetable']
  if (pathname.includes('/academic/examinations/room-management')) return ['Examination Management', 'Exam Room Management']
  if (pathname.includes('/academic/examinations/seating-rules')) return ['Examination Management', 'Seating Rules']
  if (pathname.includes('/academic/examinations/room-allocation')) return ['Examination Management', 'Room Allocation']
  if (pathname.includes('/academic/examinations/seating-plan')) return ['Examination Management', 'Seating Plan']
  if (pathname.includes('/academic/examinations/hall-ticket')) return ['Examination Management', 'Hall Ticket']
  if (pathname.includes('/academic/examinations/report-card-generation')) return ['Examination Management', 'Report Card Generation']
  if (pathname.includes('/admin-library/reports/circulation')) return ['Library', 'Reports', 'Circulation Report']
  if (pathname.includes('/admin-library/reports/collection')) return ['Library', 'Reports', 'Collection & Inventory Report']
  if (pathname.includes('/admin-library/reports/fine-overdue')) return ['Library', 'Reports', 'Fine & Overdue Report']
  if (pathname.includes('/admin-library/reports/reservation')) return ['Library', 'Reports', 'Reservation Report']
  if (pathname.includes('/admin-library/reports')) return ['Library', 'Reports']
  if (pathname.includes('/admin-library/fine-management/member-details')) return ['Library', 'Fine Management', 'Member Details']
  if (pathname.includes('/admin-library/fine-management')) return ['Library', 'Members & Fines']
  if (pathname.includes('/admin-library/reserve-book/confirm-single')) return ['Library', 'Issue & Return', 'Reserve Book', 'Confirm']
  if (pathname.includes('/admin-library/reserve-book/confirm-multi')) return ['EduAdmin', 'Circulation Node', 'Reservations']
  if (pathname.includes('/admin-library/reserve-book/select-copies')) return ['Library', 'Issue & Return', 'Reserve Books', 'Select Copies']
  if (pathname.includes('/admin-library/reserve-book/select-member')) return ['Library', 'Issue & Return', 'Reserve Books', 'Select Member']
  if (pathname.includes('/admin-library/reserve-book/reservation-confirmation')) return ['Library', 'Issue & Return', 'Reserve Book', 'Confirmation']
  if (pathname.includes('/admin-library/reserve-book')) return ['Library', 'Issue & Return', 'Reserve Book']
  if (pathname.includes('/admin-library/configuration')) return ['Library', 'Configuration']
  if (pathname.match(/\/(library-management|admin-library)\/catalogue\/[^/]+/)) return ['Library', 'Library Catalogue', 'Book Details']
  if (pathname.includes('/admin-library/catalogue')) return ['Library', 'Catalogue']
  if (pathname.includes('/admin-library') || pathname.includes('/library-management')) return ['Library', 'Dashboard']
  if (pathname.includes('/leave-management')) return ['Leave Management', 'Leave Management']
  if (pathname.includes('/communication/view-report')) return ['Communication', 'Delivery Report']
  if (pathname.includes('/communication/message')) return ['Communication', 'Message']
  if (pathname.includes('/communication/notices')) return ['Communication', 'Notices']
  if (pathname.includes('/communication/groups')) return resolveGroupBreadcrumb(pathname)
  if (pathname.includes('/communication')) return ['Communication', 'Communication']
  if (pathname.includes('/events')) return ['Communication', 'Events']
  if (pathname.includes('/user-management/role-management')) return ['Admin', 'User Management', 'Role Management']
  if (pathname.includes('/user-management/departments')) return ['User & Role Management', 'Departments']
  if (pathname.includes('/user-management/designations')) return ['User & Role Management', 'Designations']
  if (pathname.includes('/user-management')) return ['User & Role Management', 'User Management']
  if (pathname.includes('/transport/student-allocation/allocate')) return ['Transport Management', 'Student Allocation', 'Allocate Student']
  if (pathname.includes('/transport/student-allocation')) return ['Transport Management', 'Student Allocation']
  if (pathname.includes('/transport/fee-bands/setup')) return ['Transport Management', 'Transport Fee Bands', 'Fee Band Setup']
  if (pathname.includes('/transport/fee-bands/student-breakdown')) return ['Transport Management', 'Transport Fee Bands', 'Student-wise Band Breakdown']
  if (pathname.includes('/transport/fee-bands') || pathname.includes('/transport/fee-bands-configuration')) return ['Transport Management', 'Fee Band Configuration']
  if (pathname.includes('/transport/routes')) return ['Transport Management', 'Route Management']
  if (pathname.includes('/transport')) return ['Transport Management', 'Transport']
  if (pathname.includes('/settings')) return ['System', 'School Settings']
  if (pathname.includes('/profile')) return ['System', 'Admin Profile']
  return ['Main', 'Dashboard']
}

function resolveGroupBreadcrumb(pathname: string): string[] {
  // Normalize path to strip leading /admin or /teacher prefix if present
  const cleanPath = pathname.replace(/^\/(admin|teacher)/, '')
  
  // Exact /communication/groups
  if (cleanPath === '/communication/groups' || cleanPath === '/communication/groups/') {
    return ['Communication', 'Groups']
  }

  // /communication/groups/create
  if (cleanPath === '/communication/groups/create') {
    return ['Communication', 'Groups', 'Create Group']
  }

  // Match /communication/groups/:groupId/:subPage
  const match = cleanPath.match(/^\/communication\/groups\/([^/]+)(?:\/(chat|members|edit|settings))?/)
  if (match) {
    const groupId = match[1]
    const subPage = match[2]
    
    // Resolve dynamic group name from storage or fallback
    let groupName = 'Group'
    try {
      const stored = window.localStorage.getItem('school-communication-groups')
      if (stored) {
        const groups = JSON.parse(stored)
        const found = groups.find((g: { id: string; name: string }) => g.id === groupId)
        if (found?.name) groupName = found.name
      }
    } catch {
      // fallback
    }

    if (groupName === 'Group') {
      if (groupId === 'jeffs-frontend-team') groupName = 'Jeffs Frontend Team'
      else if (groupId === 'jeffs-group') groupName = 'Jeffs Group'
      else if (groupId === 'grade-10-a-students') groupName = 'Class 10-A Official'
      else if (groupId === 'science-dept-teachers') groupName = 'Science Faculty'
      else groupName = groupId.split('-').map(s => s.charAt(0).toUpperCase() + s.slice(1)).join(' ')
    }

    if (subPage === 'edit') {
      return ['Communication', 'Groups', 'Edit Group']
    }

    return ['Communication', 'Groups', groupName]
  }

  return ['Communication', 'Groups']
}

export function Header({
  onMenuClick,
  pageTitle,
  breadcrumb,
  userName,
  userRole,
  avatarSrc,
  hideSidebar = false,
  hideBreadcrumb = false,
}: HeaderProps) {
  const navigate = useNavigate()
  const location = useLocation()
  const authStore = useAuthStore()
  const { data: orgProfile } = useOrganizationProfile()

  const orgLogoUrl =
    orgProfile?.logoUrl ||
    orgProfile?.logo ||
    orgProfile?.logo_url ||
    orgProfile?.organization?.logoUrl ||
    orgProfile?.branch?.logoUrl ||
    orgProfile?.shortLivedLogoUrl ||
    undefined

  const [query, setQuery] = useState('')
  const [debouncedQuery, setDebouncedQuery] = useState('')
  const [panelOpen, setPanelOpen] = useState(false)
  const [notificationsOpen, setNotificationsOpen] = useState(false)
  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false)
  const [showLogoutModal, setShowLogoutModal] = useState(false)

  const { data: unreadData } = useUnreadCount()
  const { data: notificationsResponse } = useNotifications()
  const markNotificationReadMutation = useMarkNotificationRead()
  const markAllNotificationsReadMutation = useMarkAllNotificationsRead()

  const notifications = notificationsResponse?.items ?? []
  const unreadCount = unreadData?.unreadCount ?? 0

  const markAsRead = (id: string, actionUrl?: string | null) => {
    markNotificationReadMutation.mutate(id)
    if (actionUrl) {
      navigate(actionUrl)
    }
  }

  const markAllAsRead = () => {
    markAllNotificationsReadMutation.mutate()
  }

  const isTeacherView = useMemo(() => location.pathname.startsWith('/teacher'), [location.pathname])

  const [recentSearches, setRecentSearches] = useState<string[]>(() =>
    isTeacherView
      ? ['Assignments & Homework', 'Attendance', 'Lesson Plans']
      : ['Student Directory', 'Create Staff / Teacher', 'Fees & Finance', 'Add New Student']
  )

  const wrapperRef = useRef<HTMLDivElement | null>(null)
  const headerRef = useRef<HTMLElement | null>(null)
  const profileRef = useRef<HTMLDivElement | null>(null)

  // Debounce search query for silky smooth typing responsiveness
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(query)
    }, 150)
    return () => clearTimeout(timer)
  }, [query])

  // Select active search dataset based on user workspace and RBAC permissions
  const searchItems = useMemo(() => {
    const rawItems = isTeacherView ? teacherSearchItems : adminSearchItems
    return rawItems.filter((item) => {
      // System admin or wildcard permission access
      if (authStore.role === 'admin' || authStore.hasPermission('*')) {
        return true
      }
      // Permission-based access check
      if (item.permission) {
        if (authStore.hasPermission(item.permission)) return true
        if (authStore.hasPermission('finance:view') && item.permission.startsWith('finance:')) return true
        return false
      }
      return true
    })
  }, [isTeacherView, authStore])

  const normalizedQuery = debouncedQuery.trim().toLowerCase()

  const filteredResults = useMemo(() => {
    if (!normalizedQuery) {
      return searchItems.slice(0, 8)
    }

    return searchItems.filter((item) =>
      [item.title, item.subtitle, item.type].some((value) =>
        value.toLowerCase().includes(normalizedQuery)
      )
    )
  }, [normalizedQuery, searchItems])

  // Dynamic user details resolution
  const resolvedUserName = useMemo(() => {
    if (userName) return userName
    if (authStore.user?.name) return authStore.user.name
    const storedUser = window.localStorage.getItem('school-erp-user-name')
    if (storedUser) return storedUser
    if (location.pathname.startsWith('/superadmin')) return 'John Mathew'
    if (location.pathname.startsWith('/admin')) return 'Anushka'
    return 'Rahul Sharma'
  }, [userName, authStore.user, location.pathname])

  const resolvedUserRole = useMemo(() => {
    if (userRole) return userRole
    const currentRole = (authStore.role || authStore.user?.role || authStore.user?.userType || (authStore.user as any)?.assignedRole?.roleName || '') as string
    const norm = currentRole.toLowerCase().replace(/[^a-z]/g, '')
    if (norm.includes('branchadmin')) return 'Branch Admin'
    if (currentRole === 'fee_clerk') return 'Fee Clerk'
    if (currentRole === 'finance_head_manager') return 'Fee Manager'
    if (currentRole === 'teacher' || currentRole === 'staff' || currentRole === 'STAFF') return 'Staff'
    if (currentRole === 'student') return 'Student'
    if (currentRole === 'admin') return 'Admin'
    if (currentRole === 'hr') return 'HR Manager'
    if (currentRole === 'superadmin') return 'Super Admin'

    if (location.pathname.startsWith('/superadmin')) return 'Super Admin'
    if (location.pathname.startsWith('/teacher')) return 'Staff'
    if (location.pathname.startsWith('/student')) return 'Student'
    if (location.pathname.startsWith('/hr')) return 'HR Manager'
    if (location.pathname.startsWith('/admin') || location.pathname.startsWith('/seat-planning')) return 'Admin'

    return 'Staff'
  }, [userRole, authStore.role, authStore.user, location.pathname])

  // Breadcrumb resolution
  const parsedBreadcrumb = useMemo<{ parents: string[]; current: string }>(() => {
    let rawParts: string[] = []

    if (Array.isArray(breadcrumb)) {
      rawParts = breadcrumb.filter(Boolean)
    } else if (typeof breadcrumb === 'string' && breadcrumb.trim()) {
      rawParts = breadcrumb.split(' / ').map((p) => p.trim()).filter(Boolean)
    } else {
      const path = location.pathname
      if (path.startsWith('/teacher')) {
        rawParts = getTeacherBreadcrumbParts(path)
      } else if (path.startsWith('/admin') || path.startsWith('/organization-admin') || path.startsWith('/seat-planning')) {
        rawParts = getAdminBreadcrumbParts(path)
      } else {
        rawParts = ['Dashboard', pageTitle || 'Overview']
      }
    }

    if (rawParts.length > 0) {
      const lastItem = rawParts[rawParts.length - 1]
      if (['Student', 'Teacher', 'School Admin', 'Admin', 'HR Manager', 'Super Admin'].includes(lastItem)) {
        rawParts.pop()
      }
    }

    if (rawParts.length === 0) {
      return { parents: [], current: pageTitle || 'Dashboard' }
    }

    if (rawParts.length === 1) {
      return { parents: [], current: rawParts[0] }
    }

    return {
      parents: rawParts.slice(0, -1),
      current: rawParts[rawParts.length - 1],
    }
  }, [breadcrumb, location.pathname, pageTitle])

  const prevPathRef = useRef(location.pathname)
  useEffect(() => {
    if (prevPathRef.current !== location.pathname) {
      prevPathRef.current = location.pathname
      setPanelOpen(false)
      setNotificationsOpen(false)
      setProfileDropdownOpen(false)
    }
  }, [location.pathname])

  useEffect(() => {
    const handlePointerDown = (event: MouseEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target as Node) &&
        headerRef.current &&
        !headerRef.current.contains(event.target as Node)
      ) {
        setPanelOpen(false)
        setNotificationsOpen(false)
      }

      if (
        profileRef.current &&
        !profileRef.current.contains(event.target as Node)
      ) {
        setProfileDropdownOpen(false)
      }
    }

    const handleScroll = () => {
      setPanelOpen(false)
      setProfileDropdownOpen(false)
    }

    document.addEventListener('mousedown', handlePointerDown)
    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => {
      document.removeEventListener('mousedown', handlePointerDown)
      window.removeEventListener('scroll', handleScroll)
    }
  }, [])

  const handleSettingsNavigation = () => {
    const isTeacher = authStore.role === 'teacher' || location.pathname.startsWith('/teacher')
    const actualRawRole = (authStore.user as any)?.rawRole || (authStore.user as any)?.userType || authStore.user?.role || authStore.role
    const isOrgAdmin = isOrganizationAdminRole(actualRawRole) || location.pathname.startsWith('/organization-admin')

    if (isOrgAdmin) {
      navigate('/organization-admin/settings')
    } else if (isTeacher) {
      navigate('/teacher/settings')
    } else {
      navigate('/settings')
    }
  }

  const handleProfileNavigation = () => {
    setProfileDropdownOpen(false)
    const isTeacher = authStore.role === 'teacher' || location.pathname.startsWith('/teacher')
    const actualRawRole = (authStore.user as any)?.rawRole || (authStore.user as any)?.userType || authStore.user?.role || authStore.role
    const isOrgAdmin = isOrganizationAdminRole(actualRawRole) || location.pathname.startsWith('/organization-admin')

    if (isOrgAdmin) {
      navigate('/organization-admin/profile')
    } else if (isTeacher) {
      navigate('/teacher/profile')
    } else {
      navigate('/admin/profile')
    }
  }

  const [isReverting, setIsReverting] = useState(false)
  const isActingAsBranch = typeof window !== 'undefined' && Boolean(window.sessionStorage.getItem('acting_as_branch_admin'))

  const handleRevertToOrgAdmin = async () => {
    setIsReverting(true)
    try {
      const response = await authApi.actAsBranch(null)
      if (response?.accessToken) {
        authStore.switchBranchContext(response)
      }
      if (typeof window !== 'undefined') {
        window.sessionStorage.removeItem('acting_as_branch_admin')
      }
      showToastMessage('Reverted to Organization Admin successfully.', 'success')
      navigate('/organization-admin/manage-branches')
    } catch (err: any) {
      const errorMsg =
        err?.response?.data?.message ||
        (Array.isArray(err?.response?.data?.error?.message)
          ? err.response.data.error.message.join(', ')
          : err?.response?.data?.error?.message) ||
        err?.message ||
        'Failed to revert to organization admin.'
      showToastMessage(String(errorMsg), 'error')
    } finally {
      setIsReverting(false)
    }
  }

  return (
    <header
      ref={headerRef}
      className={`fixed left-0 right-0 top-0 z-40 border-b border-[#e8edf3] bg-white shadow-[0_6px_20px_rgba(15,23,42,0.04)] ${
        hideSidebar ? 'lg:left-0' : 'lg:left-[240px]'
      } school-admin-topbar font-['Inter']`}
    >
      <div className="flex h-full items-center justify-between gap-4 px-4 py-3 sm:px-5 lg:px-5 relative">
        {/* Left Side: Menu button + Dynamic Breadcrumb Navigation */}
        <div className="flex items-center gap-3 min-w-0">
          {onMenuClick && (
            <button
              type="button"
              onClick={onMenuClick}
              className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-[#d7e3e5] bg-white text-[#2b5560] shadow-sm lg:hidden shrink-0"
              aria-label="Open sidebar"
            >
              <MenuIcon className="h-5 w-5" />
            </button>
          )}

          {/* Dynamic Breadcrumb Hierarchy — hidden on full-screen pages like group chat */}
          {!hideBreadcrumb && (
            <div className="flex items-center gap-1.5 text-[14px] leading-tight min-w-0 overflow-hidden">
              {parsedBreadcrumb.parents.map((parent, idx) => (
                <span key={`${parent}-${idx}`} className="flex items-center gap-1.5 min-w-0 shrink max-w-[110px] sm:max-w-[200px]">
                  <span className="font-normal text-slate-500 truncate" title={parent}>{parent}</span>
                  <span className="text-slate-300 font-medium shrink-0">/</span>
                </span>
              ))}
              <span className="font-bold text-black truncate min-w-0 shrink max-w-[140px] sm:max-w-[260px] md:max-w-none" title={parsedBreadcrumb.current}>
                {parsedBreadcrumb.current}
              </span>
            </div>
          )}
        </div>

        {/* Center & Right Side Container */}
        <div className="flex flex-1 items-center justify-end gap-4">
          {/* Center: Contextual Search Input */}
          <div ref={wrapperRef} className="relative hidden w-full max-w-[220px] lg:max-w-[280px] xl:max-w-[340px] md:block">
            <SearchInput
              placeholder="Search"
              className="w-full"
              variant="header"
              inputClassName="school-admin-global-search"
              value={query}
              onChange={(value) => {
                setQuery(value)
                setPanelOpen(true)
              }}
              onFocus={() => setPanelOpen(true)}
            />

            {panelOpen && (
              <div className="absolute left-0 top-[calc(100%+8px)] z-50 w-full min-w-[340px] max-w-[440px] overflow-hidden rounded-[16px] border border-[#b7dfd9] bg-[#f0f9f8] p-2 shadow-[0_16px_36px_rgba(15,23,42,0.12)] font-['Inter']">
                {query.trim() ? (
                  <div className="space-y-1">
                    <div className="px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider text-[#146971]">
                      Search Results ({filteredResults.length})
                    </div>
                    {filteredResults.length > 0 ? (
                      filteredResults.map((result) => (
                        <button
                          key={result.id}
                          type="button"
                          onClick={() => {
                            if (!recentSearches.includes(result.title)) {
                              setRecentSearches((prev) => [result.title, ...prev.slice(0, 4)])
                            }
                            navigate(result.path)
                            setPanelOpen(false)
                          }}
                          className="flex w-full items-center justify-between gap-3 rounded-xl px-3 py-2 text-left transition bg-transparent hover:bg-[#d8eee9] cursor-pointer group"
                        >
                          <div className="flex items-center gap-2.5 min-w-0">
                            <SearchIcon className="h-4 w-4 text-[#146971] shrink-0" />
                            <span className="text-[13px] font-semibold text-slate-800 truncate">
                              {result.title}
                            </span>
                          </div>
                          <span className="text-[11px] font-medium text-[#146971] bg-white px-2 py-0.5 rounded-md border border-[#c3e5df] shrink-0">
                            {result.type}
                          </span>
                        </button>
                      ))
                    ) : (
                      <div className="px-3 py-4 text-center text-[13px] text-slate-500">
                        No pages or actions found matching &quot;{query.trim()}&quot;
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="space-y-1">
                    <div className="px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider text-[#146971]/80">
                      Recent Searches
                    </div>

                    {recentSearches.length > 0 ? (
                      recentSearches.map((item) => (
                        <div
                          key={item}
                          onClick={() => {
                            setQuery(item)
                            const match = searchItems.find((i) => i.title.toLowerCase() === item.toLowerCase())
                            if (match) {
                              navigate(match.path)
                              setPanelOpen(false)
                            }
                          }}
                          className="group flex w-full items-center justify-between gap-3 rounded-xl px-3 py-2 text-left transition bg-transparent hover:bg-[#d8eee9] cursor-pointer"
                        >
                          <div className="flex items-center gap-2.5 min-w-0 flex-1">
                            <Clock className="h-3.5 w-3.5 text-slate-500 group-hover:text-[#146971] shrink-0 transition-colors" />
                            <span className="text-[13px] font-medium text-slate-700 group-hover:text-slate-900 truncate">
                              {item}
                            </span>
                          </div>

                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation()
                              setRecentSearches((prev) => prev.filter((r) => r !== item))
                            }}
                            className="p-1 text-slate-400 hover:text-slate-700 hover:bg-[#c3e5df] rounded-full opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                            title="Remove recent search"
                          >
                            <X className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      ))
                    ) : (
                      <div className="px-3 py-3 text-center text-[13px] text-slate-500">
                        No recent searches
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Right Side: Notifications, Settings, User Info & Avatar */}
          <div className="flex items-center gap-2 sm:gap-3 text-[#070707]">
            {isActingAsBranch && (
              <button
                type="button"
                disabled={isReverting}
                onClick={handleRevertToOrgAdmin}
                className="inline-flex items-center gap-1.5 rounded-full border border-amber-300 bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-800 shadow-xs transition hover:bg-amber-100 hover:border-amber-400 cursor-pointer"
                title="Exit elevated branch admin mode and return to organization admin"
              >
                <ShieldAlert className="h-3.5 w-3.5 text-amber-600 shrink-0" />
                <span className="hidden sm:inline">{isReverting ? 'Reverting...' : 'Exit Branch Admin'}</span>
                <span className="sm:hidden">{isReverting ? '...' : 'Exit'}</span>
              </button>
            )}

            {/* Bell Icon with Red Dot Badge */}
            <div className="relative">
              <button
                type="button"
                onClick={() => setNotificationsOpen((prev) => !prev)}
                className="relative inline-flex h-9 w-9 items-center justify-center rounded-full transition hover:bg-slate-100 cursor-pointer"
                aria-label="Open notifications"
              >
                <Bell className="h-5 w-5 text-slate-600 stroke-[1.75]" />
                {unreadCount > 0 && (
                  <span className="absolute right-[8px] top-[7px] h-2.5 w-2.5 rounded-full bg-[#ff3b3b] ring-2 ring-white" />
                )}
              </button>

              <NotificationPanel
                open={notificationsOpen}
                onClose={() => setNotificationsOpen(false)}
                notifications={notifications}
                onMarkAsRead={markAsRead}
                onMarkAllAsRead={markAllAsRead}
              />
            </div>

            {/* Settings Icon (Role-based Navigation) */}
            <button
              type="button"
              onClick={handleSettingsNavigation}
              className="inline-flex h-9 w-9 items-center justify-center rounded-full transition hover:bg-slate-100 cursor-pointer"
              aria-label="Settings"
            >
              <Settings className="h-5 w-5 text-slate-600 stroke-[1.75]" />
            </button>

            <div className="h-8 w-px bg-slate-200" />

            {/* User Profile Container & Dropdown Trigger */}
            <div ref={profileRef} className="relative">
              <div
                onClick={() => setProfileDropdownOpen((prev) => !prev)}
                className="flex items-center gap-3 cursor-pointer p-1 rounded-xl hover:bg-slate-100/70 transition"
              >
                <div className="text-right hidden sm:block shrink-0 min-w-max">
                  <p
                    style={{ color: '#191C1D' }}
                    className="school-admin-user-role font-['Inter'] text-[14px] font-bold leading-tight whitespace-nowrap"
                  >
                    {resolvedUserRole === 'School Admin' ? 'Admin' : resolvedUserRole}
                  </p>
                  <p
                    style={{ color: '#3F484A' }}
                    className="school-admin-user-name font-['Inter'] text-[12px] font-medium leading-tight mt-0.5 whitespace-nowrap"
                  >
                    {resolvedUserName}
                  </p>
                </div>

                <Avatar
                  name={resolvedUserName}
                  src={orgLogoUrl || avatarSrc || (authStore.user as any)?.avatarUrl || (authStore.user as any)?.avatar || undefined}
                  size="sm"
                  className="!h-9 !w-9 object-cover rounded-full shrink-0 shadow-xs border border-slate-200"
                />
              </div>

              {/* PROFILE DROPDOWN MENU */}
              {profileDropdownOpen && (
                <div className="absolute right-0 top-[calc(100%+8px)] z-50 w-52 overflow-hidden rounded-[18px] border border-[#d2e5e2] bg-white p-2 shadow-[0_12px_32px_rgba(15,23,42,0.12)] animate-in fade-in zoom-in-95 duration-150 font-['Inter']">
                  <div className="space-y-1">
                    {isActingAsBranch && (
                      <button
                        type="button"
                        disabled={isReverting}
                        onClick={() => {
                          setProfileDropdownOpen(false)
                          handleRevertToOrgAdmin()
                        }}
                        className="flex w-full items-center gap-3 rounded-[12px] bg-amber-50 px-4 py-2.5 text-[14px] font-semibold text-amber-900 transition cursor-pointer hover:bg-amber-100 group"
                      >
                        <ShieldAlert className="h-5 w-5 text-amber-600 shrink-0" />
                        <span>{isReverting ? 'Reverting...' : 'Exit Branch Admin'}</span>
                      </button>
                    )}

                    <button
                      type="button"
                      onClick={handleProfileNavigation}
                      className="flex w-full items-center gap-3 rounded-[12px] bg-transparent px-4 py-2.5 text-[14px] font-medium text-slate-700 transition cursor-pointer hover:bg-[#bfe6e2] hover:text-[#146971] group"
                    >
                      <User className="h-5 w-5 text-slate-600 transition-colors group-hover:text-[#146971] shrink-0" />
                      <span>View profile</span>
                    </button>

                    <button
                      type="button"
                      onClick={() => {
                        setProfileDropdownOpen(false)
                        setShowLogoutModal(true)
                      }}
                      className="flex w-full items-center gap-3 rounded-[12px] bg-transparent px-4 py-2.5 text-[14px] font-medium text-slate-700 transition cursor-pointer hover:bg-[#bfe6e2] hover:text-[#146971] group"
                    >
                      <LogOut className="h-5 w-5 text-slate-600 transition-colors group-hover:text-[#146971] shrink-0" />
                      <span>Logout</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <LogoutConfirmModal
        open={showLogoutModal}
        onClose={() => setShowLogoutModal(false)}
        onConfirm={() => {
          setShowLogoutModal(false)
          authStore.logout()
          navigate('/login')
        }}
      />
    </header>
  )
}

export default Header
